export const freeTrialDataFields = {
	firstName: 'firstName',
	lastName: 'lastName',
	emailId: 'emailId',
	companyName: 'companyName',
	domain: 'domain',
	password: 'password',
	isLicense: 'isLicense',
	isSSO: 'isSSO',
	product: 'product',
};

export const freeTrialProducts = {
	unlock: 'unlock',
	oneGuru: 'oneGuru',
};

/**
 * Domain suffix appended to the sub domain entered by the user, per product.
 * The key is stored in the form data and the suffix is used both for the
 * field adornment and for the domain sent to the API.
 */
export const freeTrialProductList = [
	{ id: freeTrialProducts.unlock, name: 'Unlock', domainSuffix: '.unlockokr.com' },
	{ id: freeTrialProducts.oneGuru, name: 'OneGuru', domainSuffix: '.oneguru.ai' },
];

export const getProductDomainSuffix = (product: string) => {
	const selected = freeTrialProductList.find((item) => item.id === product);
	return selected ? selected.domainSuffix : freeTrialProductList[0].domainSuffix;
};

/** OneGuru sign ups are booked through a separate API. */
export const isOneGuruProduct = (product: string) => product === freeTrialProducts.oneGuru;

/** Builds the fully qualified domain, e.g. `demo` + oneGuru => `demo.oneguru.ai` */
export const getFullDomain = (subDomain: string, product: string) => {
	const trimmed = (subDomain || '').trim();
	return trimmed === '' ? '' : `${trimmed}${getProductDomainSuffix(product)}`;
};

/**
 * Domain sent when booking a demo. OneGuru expects the bare sub domain and
 * appends its own suffix server side, while Unlock expects the full domain.
 */
export const getBookDemoDomain = (subDomain: string, product: string) =>
	isOneGuruProduct(product) ? subDomain : getFullDomain(subDomain, product);

export interface FreeTrialDataProps {
	firstName: string;
	lastName: string;
	emailId: string;
	companyName: string;
	domain: string;
	password: string;
	isLicense: boolean;
	isSSO: boolean;
}

export const defaultFreeTrialData: any = {
	firstName: '',
	lastName: '',
	emailId: '',
	companyName: '',
	domain: '',
	password: '',
	isLicense: false,
	isSSO: false,
	product: freeTrialProducts.unlock,
};

export const freeTrialDataRequiredFields = ['firstName', 'lastName', 'emailId', 'companyName', 'domain', 'password'];

export const FreeTrialRequiredFieldsErrorMsg: any = {
	firstName: 'firstNameRequired',
	lastName: 'lastNameRequired',
	emailId: 'businessEmailRequired',
	companyName: 'businessNameRequired',
	domain: 'businessDomainNameRequired',
	password: 'passwordRequired',
	emailIdInvalid: '',
	domainExists: '',
};

export const isFieldValid = async (field: string, fieldValue: any, t: any, apis: any, product?: string) => {
	switch (field) {
		case freeTrialDataFields.firstName:
		case freeTrialDataFields.lastName:
		case freeTrialDataFields.companyName:
			if (fieldValue.trim() === '') {
				return t(FreeTrialRequiredFieldsErrorMsg[field]);
			}
			return '';
		case freeTrialDataFields.domain:
			if (fieldValue.trim() === '') {
				return t(FreeTrialRequiredFieldsErrorMsg[field]);
			} else {
				const api = apis.domain;
				return await api({ domain: getFullDomain(fieldValue, product || defaultFreeTrialData.product) });
			}
			return '';
		case freeTrialDataFields.emailId:
			if (fieldValue.trim() === '') {
				return t(FreeTrialRequiredFieldsErrorMsg[field]);
			} else {
				const api = apis.email;
				return await api({ emailId: fieldValue });
			}
			return '';
		case freeTrialDataFields.password:
			if (fieldValue.trim() === '') {
				return t(FreeTrialRequiredFieldsErrorMsg[field]);
			} else {
				const api = apis.password;
				return await api({ password: fieldValue });
			}
			return '';
		default:
			return '';
	}
};

export const prepareApiPayload = (payload: any): FreeTrialDataProps => {
	// Built field by field so form-only state (e.g. the selected product) is not posted.
	const createFreeTrialPayload: FreeTrialDataProps = {
		firstName: payload.firstName,
		lastName: payload.lastName,
		emailId: payload.emailId,
		companyName: payload.companyName,
		domain: getBookDemoDomain(payload.domain, payload.product),
		password: payload.password,
		isLicense: payload.isLicense,
		isSSO: payload.isSSO,
	};
	return createFreeTrialPayload;
};
